import React from 'react';
import { StockStatusSplit } from "@/routes/(pharma)/_layout/inventory/-data/model";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { PieChart, Pie, Cell } from "recharts";
import { StockStatusDistribution } from '@/gen/inventory/common_pb';

function formatStatusLabel(label: string) {
  return label
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getStatusColor(status: string) {
  if (status === 'in_stock') return '#15803d'; // dark green
  if (status === 'out_of_stock') return '#ef4444'; // red
  if (status === 'low_stock') return '#facc15'; // yellow
  return '#64748b'; // slate-500 fallback
}

interface StockStatusDonutChartProps {
  stockStatusSplit: StockStatusDistribution[];
}

const StockStatusDonutChart: React.FC<StockStatusDonutChartProps> = ({ stockStatusSplit }) => {
  const total = stockStatusSplit.reduce((sum, s) => sum + s.itemCount, 0);
  return (
    <Card className="bg-[#FCFCFC] flex flex-col h-full">
      <CardHeader className="pb-0 text-center">
        <CardTitle className="text-center">Stock Status Distribution</CardTitle>
        <CardDescription className="text-center">Current stock distribution</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-0">
        <ChartContainer
          config={{}}
          className="mx-auto aspect-square max-h-[250px]"
        >
          <PieChart width={300} height={300}>
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel formatter={(value, name, props) => {
                let percent = '';
                if (props && typeof props === 'object' && 'payload' in props && typeof props.payload.percent === 'number') {
                  percent = (props.payload.percent * 100).toFixed(1);
                }
                return [
                  percent ? `${value} (${percent}%)` : `${value}`,
                  formatStatusLabel(name as string)
                ];
              }} />}
            />
            <Pie
              data={stockStatusSplit.map(entry => ({
                status: entry.status,
                itemCount: entry.itemCount,
                percent: total > 0 ? (entry.itemCount / total) : 0
              }))}
              dataKey="itemCount"
              nameKey="status"
              innerRadius={70}
              outerRadius={110}
              paddingAngle={2}
              labelLine={false}
            >
              {stockStatusSplit.map((entry) => (
                <Cell key={entry.status} fill={getStatusColor(entry.status)} />
              ))}
            </Pie>
          </PieChart>
        </ChartContainer>
        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-4 my-4">
          {stockStatusSplit.map((entry) => {
            const percent = total > 0 ? ((entry.itemCount / total) * 100).toFixed(1) : '0.0';
            return (
              <div key={entry.status} className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: getStatusColor(entry.status) }} />
                <span className="text-xs font-medium">{formatStatusLabel(entry.status)}</span>
                <span className="text-xs text-muted-foreground">{entry.itemCount} ({percent}%)</span>
              </div>
            );
          })}
        </div>
      </CardContent>
      {/* <CardFooter className="flex-col gap-2 text-sm items-center">
        <div className="leading-none text-muted-foreground text-center">
          See your stock status distribution at a glance.
        </div>
      </CardFooter> */}
    </Card>
  );
};

export default StockStatusDonutChart; 